import { useClientTerminology } from '@/views/list/NetworkTerms';
import { FilterSection } from './FilterSection';
import { type Species } from '@/features/fieldSlice';
import {
  useHasFuelsData,
  useHasWildfireObjective,
} from '@/hooks/useWildfireMode';
import { OverhangFilter } from './OverhangFilter';
import { TagsFilter } from './TagsFilter';
import { TreesToggle } from './TreesToggle';
import { WildfireZoneFilter } from './WildfireZoneFilter';
import { SpeciesFilter } from './SpeciesFilter';
import { PriorityRatingFilter } from './PriorityRatingFilter';
import { EncroachmentRiskFilter } from './EncroachmentRiskFilter';
import { useUserClientContext } from '@/app/clientContext';
import { isPge } from '@/features/utils';
import type { Control } from 'react-hook-form';
import type { FilterState } from '@/features/filterSlice';
import { RedFlagWarningFilter } from './RedFlagWarningFilter';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import { useAppFlags } from '@/hooks/useAppFlags';
import type { TagField } from '@/features/types';
import { WildfirePriorityRatingFilter } from '@/views/filter/WildfirePriorityRatingFilter';
import { FuelHazardRatingFilter } from './FuelHazardRatingFilter';
import { FuelDistributionFilter } from './FuelDistributionFilter';

export const RiskAndVegetationFilters = (props: {
  control: Control<FilterState>;
  speciesData: Species[] | undefined;
  hasWildfireRisk: boolean | undefined;
  hasHazardTrees: boolean | undefined;
  hasStrikeTrees: boolean | undefined;
  hasCriticalTrees: boolean | undefined;
  hasOverhang: boolean | undefined;
  hasFallInOptionSelected: boolean | undefined;
  tags: TagField[] | undefined;
  redFlagWarnings: RedFlagWarning[] | undefined;
  showTitle?: boolean;
}) => {
  const intl = useClientTerminology();
  const { getClientTerm } = intl;
  const { context } = useUserClientContext();
  const { flagRedFlagWarning } = useAppFlags();

  const displayWildfirePriorityRatingFilter = useHasWildfireObjective();
  const hasFuelsData = useHasFuelsData();

  return (
    <FilterSection
      title={
        props.showTitle !== false
          ? intl.formatMessage({ id: 'filter.riskAndVegetationSection' })
          : undefined
      }
    >
      {displayWildfirePriorityRatingFilter && (
        <WildfirePriorityRatingFilter control={props.control} />
      )}
      {!isPge(context) && <PriorityRatingFilter control={props.control} />}
      {!props.hasFallInOptionSelected && (
        <EncroachmentRiskFilter control={props.control} />
      )}
      {props.hasOverhang && !props.hasFallInOptionSelected && (
        <OverhangFilter control={props.control} />
      )}
      {props.hasHazardTrees && (
        <TreesToggle
          control={props.control}
          fieldName="hasHazardTrees"
          title={getClientTerm('filter.hazardTreesTitle')}
          label={getClientTerm('filter.hazardTreesLabel')}
        />
      )}
      {props.hasStrikeTrees && (
        <TreesToggle
          control={props.control}
          fieldName="hasStrikeTrees"
          title={getClientTerm('filter.strikeTreesTitle')}
          label={getClientTerm('filter.strikeTreesLabel')}
        />
      )}
      {props.hasCriticalTrees && (
        <TreesToggle
          control={props.control}
          fieldName="hasCriticalTrees"
          title={getClientTerm('filter.criticalTreesTitle')}
          label={getClientTerm('filter.criticalTreesLabel')}
        />
      )}
      {props.speciesData && !props.hasFallInOptionSelected && (
        <SpeciesFilter control={props.control} data={props.speciesData} />
      )}
      {props.hasWildfireRisk && <WildfireZoneFilter control={props.control} />}
      {props.hasWildfireRisk && props.redFlagWarnings && flagRedFlagWarning && (
        <RedFlagWarningFilter
          control={props.control}
          redFlagWarnings={props.redFlagWarnings}
        />
      )}
      {props.tags !== undefined && (
        <TagsFilter control={props.control} data={props.tags} />
      )}
      {hasFuelsData && <FuelHazardRatingFilter control={props.control} />}
      {hasFuelsData && <FuelDistributionFilter control={props.control} />}
    </FilterSection>
  );
};
